import { getYear, getMonth, getDaysInMonth, getDate, addYears, addDays } from 'date-fns';

import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    output,
    viewChild,
    input,
    inject
} from '@angular/core';

import { ApDateService } from '@ap/date';

import { ApCalendarBodyComponent, ApCalendarCell } from '../body/calendar-body.component';

export const yearsPerPage = 24;
export const yearsPerRow = 4;

/**
 * An internal component used to display a year selector in the datepicker.
 *
 * @docs-private
 */
@Component({
    selector: 'multi-year-view',
    templateUrl: './multi-year-view.component.html',
    styleUrls: ['../calendar.component.scss'],
    exportAs: 'apMultiYearView',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ApCalendarBodyComponent]
})
export class ApMultiYearViewComponent implements AfterContentInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    /** The date to display in this multi-year view (everything other than the year is ignored). */
    @Input()
    get activeDate(): Date {
        return this._activeDate;
    }

    set activeDate(value: Date) {
        const oldActiveDate = this._activeDate;
        const validDate = ApDateService.getValidDateOrNull(ApDateService.utc(value)) || ApDateService.utc();
        this._activeDate = ApDateService.clampDate(validDate, this.minDate(), this.maxDate());
        if (Math.floor(getYear(oldActiveDate) / yearsPerPage) !== Math.floor(getYear(this._activeDate) / yearsPerPage)) {
            this._init();
            this.activeDateChange.emit(this._activeDate);
        }
    }

    /** The currently selected date. */
    @Input()
    get selected(): Date | null {
        return this._selected;
    }

    set selected(value: Date | null) {
        this._selected = ApDateService.getValidDateOrNull(ApDateService.utc(value));
        this._selectedYear = this._selected && getYear(this._selected);
    }

    /** The minimum selectable date. */
    public readonly minDate = input(undefined, {
        transform: (value: Date | null): Date | null => {
            return ApDateService.getValidDateOrNull(ApDateService.utc(value));
        }
    });

    /** The maximum selectable date. */
    public readonly maxDate = input(undefined, {
        transform: (value: Date | null): Date | null => {
            return ApDateService.getValidDateOrNull(ApDateService.utc(value));
        }
    });

    /** A function used to filter which dates are selectable. */
    readonly dateFilter = input<(date: Date) => boolean>(undefined);
    /** Emits when a new year is selected. */
    readonly selectedChange = output<Date>();
    /** Emits the selected year. This doesn't imply a change on the selected date */
    readonly yearSelected = output<Date>();
    /** Emits when any date is activated. */
    readonly activeDateChange = output<Date>();
    /** The body of calendar table */
    readonly _apCalendarBody = viewChild(ApCalendarBodyComponent);
    /** Grid of calendar cells representing the currently displayed years. */
    _years: ApCalendarCell[][];
    /** The year that today falls on. */
    _todayYear: number;
    /** The year of the selected date. Null if the selected date is null. */
    _selectedYear: number | null;

    private _activeDate: Date;

    private _selected: Date | null;

    constructor() {
        this._activeDate = ApDateService.utc();
    }

    ngAfterContentInit() {
        this._init();
    }

    /** Initializes this multi-year view. */
    _init() {
        this._todayYear = getYear(ApDateService.utc());
        const activeYear = getYear(this._activeDate);
        const activeOffset = activeYear % yearsPerPage;
        this._years = [];
        for (let i = 0, row: number[] = []; i < yearsPerPage; i++) {
            row.push(activeYear - activeOffset + i);
            if (row.length === yearsPerRow) {
                this._years.push(row.map((year) => this._createCellForYear(year)));
                row = [];
            }
        }
        this.changeDetectorRef.markForCheck();
    }

    /** Handles when a new year is selected. */
    _yearSelected(year: number) {
        this.yearSelected.emit(ApDateService.utc(new Date(year, 0, 1)));
        const month = getMonth(this.activeDate);
        const daysInMonth = getDaysInMonth(ApDateService.utc(new Date(year, month, 1)));
        this.selectedChange.emit(ApDateService.utc(new Date(year, month, Math.min(getDate(this.activeDate), daysInMonth))));
    }

    /** Handles keydown events on the calendar body when calendar is in multi-year view. */
    _handleCalendarBodyKeydown(event: KeyboardEvent): void {
        // TODO(mmalerba): We currently allow keyboard navigation to disabled dates, but just prevent
        // disabled ones from being selected. This may not be ideal, we should look into whether
        // navigation should skip over disabled dates, and if so, how to implement that efficiently.
        // https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/Code

        const activeYear = getYear(this._activeDate);

        switch (event.key) {
            case 'ArrowLeft':
                this.activeDate = addYears(this._activeDate, -1);
                break;
            case 'ArrowRight':
                this.activeDate = addYears(this._activeDate, 1);
                break;
            case 'ArrowUp':
                this.activeDate = addYears(this._activeDate, -yearsPerRow);
                break;
            case 'ArrowDown':
                this.activeDate = addYears(this._activeDate, yearsPerRow);
                break;
            case 'Home':
                this.activeDate = addYears(this._activeDate, -activeYear % yearsPerPage);
                break;
            case 'End':
                this.activeDate = addYears(this._activeDate, yearsPerPage - (activeYear % yearsPerPage));
                break;
            case 'PageUp':
                this.activeDate = addYears(this._activeDate, event.altKey ? -yearsPerPage * 10 : -yearsPerPage);
                break;
            case 'PageDown':
                this.activeDate = addYears(this._activeDate, event.altKey ? yearsPerPage * 10 : yearsPerPage);
                break;
            case 'Enter':
                this._yearSelected(activeYear);
                break;
            default:
                // Don't prevent default or focus active cell on keys that we don't explicitly handle.
                return;
        }

        this._focusActiveCell();
        // Prevent unexpected default actions such as form submission.
        event.preventDefault();
    }

    _getActiveCell(): number {
        return getYear(this.activeDate) % yearsPerPage;
    }

    /** Focuses the active cell after the microtask queue is empty. */
    _focusActiveCell(): void {
        this._apCalendarBody()._focusActiveCell();
    }

    /** Creates an ApCalendarCell for the given year. */
    private _createCellForYear(year: number): ApCalendarCell {
        const yearName = ApDateService.format(ApDateService.utc(new Date(year, 0, 1)), 'yyyy');
        return new ApCalendarCell(year, yearName, yearName, this._shouldEnableYear(year));
    }

    /** Whether the given year is enabled. */
    private _shouldEnableYear(year: number): boolean {
        // disable if the year is greater than maxDate lower than minDate
        if (
            year === undefined ||
            year === null ||
            (this.maxDate() && year > getYear(this.maxDate())) ||
            (this.minDate() && year < getYear(this.minDate()))
        ) {
            return false;
        }

        // enable if it reaches here and there's no filter defined
        const dateFilter = this.dateFilter();
        if (!dateFilter) {
            return true;
        }

        const firstOfYear = ApDateService.utc(new Date(year, 0, 1));

        // If any date in the year is enabled count the year as enabled.
        for (let date = firstOfYear; getYear(date) === year; date = addDays(date, 1)) {
            if (dateFilter(date)) {
                return true;
            }
        }

        return false;
    }
}
